import { useEffect, useState } from 'react';
import { streamerAccountApi } from '../api';
import './RedeemedCoins.css';

const FILTERS = [
  { key: 'unredeemed', label: 'Uneingelöste Coins' },
  { key: 'redeemed', label: 'Eingelöste Coins' },
];

export default function RedeemedCoins() {
  const [payouts, setPayouts] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);
  const [expandedStreamer, setExpandedStreamer] = useState(null);
  const [expandedWallet, setExpandedWallet] = useState(null);
  const [filter, setFilter] = useState('redeemed');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [payoutData, walletData] = await Promise.all([
          streamerAccountApi.getPayouts(),
          streamerAccountApi.getWallets(),
        ]);
        setPayouts(Array.isArray(payoutData) ? payoutData : []);
        setWallets(Array.isArray(walletData) ? walletData : []);
      } catch (e) {
        setError(e?.response?.data?.error || 'failed_to_load');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleConfirm = async (redemptionId) => {
    setConfirmingId(redemptionId);
    try {
      await streamerAccountApi.confirmPayout(redemptionId);

      let confirmedByName = 'Unknown';
      try {
        const token = localStorage.getItem('discord_token');
        if (token) {
          const userRes = await fetch(`${process.env.REACT_APP_API_BASE || '/api'}/user-data`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const userData = await userRes.json();
          confirmedByName = userData?.global_name || userData?.username || 'Unknown';
        }
      } catch (e) {
        console.error('Failed to fetch user name for confirmation:', e);
      }

      setPayouts(prev => prev.map(p => p.id === redemptionId ? {
        ...p,
        payout_status: 'paid',
        payout_confirmed_at: new Date().toISOString(),
        payout_confirmed_by_name: confirmedByName,
      } : p));
    } catch (e) {
      setError(e?.response?.data?.error || 'confirm_failed');
    } finally {
      setConfirmingId(null);
    }
  };

  const redemptionGroups = Object.values(
    payouts.reduce((acc, item) => {
      const key = item.streamer_id || item.discord_id;
      if (!acc[key]) {
        acc[key] = {
          streamer_id: item.streamer_id,
          streamer_name: item.streamer_name,
          display_name: item.display_name,
          username: item.username,
          platform: item.platform,
          status: item.status,
          items: [],
        };
      }
      acc[key].items.push(item);
      return acc;
    }, {})
  )
    .map(group => ({
      ...group,
      totalCoins: group.items.reduce((sum, item) => sum + (item.redeemed_coins || 0), 0),
    }))
    .sort((a, b) => (b.streamer_name || '').localeCompare(a.streamer_name || ''));

  if (loading) {
    return (
      <div className="page redeemed-coins-page">
        <div className="loading-container">
          <div className="spinner" />
          <p className="muted">Lädt...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page redeemed-coins-page">
        <h1>Coins Übersicht</h1>
        <p className="muted">Zugriff verweigert.</p>
      </div>
    );
  }

  const walletContent = wallets.length === 0 ? (
    <div className="redeemed-coins-card">
      <p className="muted">Keine uneingelösten Coins vorhanden.</p>
    </div>
  ) : (
    <div className="redeemed-coins-list">
      {wallets.map((w) => {
        const totalCoins = w.coins || 0;
        const isExpanded = expandedWallet === w.username;

        const streamerPayouts = payouts.filter(
          (p) => (p.username || p.streamer_name) === w.username
        );
        const pendingPayouts = streamerPayouts.filter(
          (p) => p.payout_status !== 'paid'
        );
        const totalPending = pendingPayouts.reduce(
          (sum, p) => sum + (p.redeemed_coins || 0),
          0
        );
        const platforms = w.platforms
          ? w.platforms.split(',').join(', ')
          : w.platform || '';

        return (
          <div
            key={w.username || w.display_name || w.streamer_id}
            className="redeemed-coins-card"
          >
            <div
              className="redeemed-coins-header"
              onClick={() =>
                setExpandedWallet(isExpanded ? null : w.username)
              }
            >
              <div className="redeemed-coins-avatar">
                {(w.display_name || w.username || '?')[0]}
              </div>
              <div>
                <h3>{w.display_name || w.username || 'Unbekannt'}</h3>
                <p className="muted">
                  @{w.username || ''} • {platforms || ''}
                </p>
              </div>
              <div className="redeemed-coins-header-meta">
                <span
                  className={`redeemed-coins-status ${
                    w.status === 'live' ? 'live' : 'offline'
                  }`}
                >
                  {w.status === 'live' ? 'Live' : 'Offline'}
                </span>
                <span className="redeemed-coins-toggle">
                  {isExpanded ? '▲' : '▼'}
                </span>
              </div>
            </div>

            {isExpanded && (
              <div className="redeemed-coins-history">
                <div className="redeemed-coins-history-meta">
                  <span className="redeemed-coins-amount">
                    {totalCoins} Coins im Wallet
                  </span>
                  <span className="muted">
                    Letzte Stundenzahlung:{' '}
                    {w.last_hourly_reward
                      ? new Date(w.last_hourly_reward).toLocaleString('de-DE')
                      : 'Noch keine'}
                  </span>
                  <span className="muted">Plattformen: {platforms || '—'}</span>
                </div>

                {pendingPayouts.length > 0 && (
                  <div className="redeemed-coins-history-item">
                    <div className="redeemed-coins-history-main">
                      <span className="redeemed-coins-amount">
                        {totalPending} Coins ausstehend
                      </span>
                      <span className="muted">
                        {pendingPayouts.length} Auszahlung(en) ausstehend
                      </span>
                      <span className="redeemed-coins-payout-badge pending">
                        Ausstehend
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const redemptionContent = redemptionGroups.length === 0 ? (
    <div className="redeemed-coins-card">
      <p className="muted">Keine eingelösten Coins vorhanden.</p>
    </div>
  ) : (
    <div className="redeemed-coins-list">
      {redemptionGroups.map((group) => {
        const totalCoins = group.totalCoins;
        const isExpanded = expandedStreamer === group.streamer_id;

        return (
          <div key={group.streamer_id || group.streamer_name} className="redeemed-coins-card">
            <div className="redeemed-coins-header" onClick={() => setExpandedStreamer(isExpanded ? null : group.streamer_id)}>
              <div className="redeemed-coins-avatar">
                {(group.display_name || group.streamer_name || '?')[0]}
              </div>
              <div>
                <h3>{group.display_name || group.streamer_name || 'Unbekannt'}</h3>
                <p className="muted">@{group.username || group.streamer_name || ''} • {group.platform || ''}</p>
              </div>
              <div className="redeemed-coins-header-meta">
                <span className="redeemed-coins-total">{totalCoins} Coins gesamt</span>
                <span className={`redeemed-coins-status ${group.status === 'live' ? 'live' : 'offline'}`}>
                  {group.status === 'live' ? 'Live' : 'Offline'}
                </span>
                <span className="redeemed-coins-toggle">{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>

            {isExpanded && (
              <div className="redeemed-coins-history">
                {group.items.map((item) => (
                  <div key={item.id} className="redeemed-coins-history-item">
                    <div className="redeemed-coins-history-main">
                      <span className="redeemed-coins-amount">{item.redeemed_coins || 0} Coins</span>
                      <span className="redeemed-coins-date">
                        {item.redeemed_at ? new Date(item.redeemed_at).toLocaleString('de-DE') : ''}
                      </span>
                      <span className={`redeemed-coins-payout-badge ${item.payout_status === 'paid' ? 'paid' : 'pending'}`}>
                        {item.payout_status === 'paid' ? 'Ausgezahlt' : 'Ausstehend'}
                      </span>
                    </div>
                    {item.payout_status !== 'paid' && (
                      <div className="redeemed-coins-history-actions">
                        <button
                          onClick={() => handleConfirm(item.id)}
                          disabled={confirmingId === item.id}
                          className="btn btn-outline btn-small"
                        >
                          {confirmingId === item.id ? 'Bestätige...' : 'Als ausgezahlt bestätigen'}
                        </button>
                      </div>
                    )}
                    {item.payout_status === 'paid' && (
                      <div className="redeemed-coins-history-meta">
                        <span className="muted">
                          Bestätigt von: {item.payout_confirmed_by_name || item.payout_confirmed_by || 'Unknown'} • {item.payout_confirmed_at ? new Date(item.payout_confirmed_at).toLocaleString('de-DE') : ''}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="page redeemed-coins-page">
      <h1></h1>
      <p className="redeemed-coins-subtitle">Coins der Streamer nach Status</p>

      <div className="coins-filters">
        {FILTERS.map((f) => {
          const count = f.key === 'redeemed' ? payouts.length : wallets.length;
          return (
            <button
              key={f.key}
              onClick={() => {
                setFilter(f.key);
                setExpandedStreamer(null);
                setExpandedWallet(null);
              }}
              className={`coins-filter-btn ${filter === f.key ? 'active' : ''}`}
            >
              <span className="coins-filter-label">{f.label}</span>
              {count > 0 && <span className="coins-filter-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {filter === 'unredeemed' ? walletContent : redemptionContent}
    </div>
  );
}
